import { supabase } from '../lib/supabase';

/**
 * Deliverable Service - Handles all deliverable-related operations
 * Replaces static 5-stage system with dynamic deliverables
 */

/**
 * Get all deliverables for a team
 * @param {string} teamId - Team UUID
 * @returns {Promise<Array>} Array of deliverables
 */
export const getTeamDeliverables = async (teamId) => {
  try {
    const { data, error } = await supabase
      ?.from('team_deliverables')
      ?.select('*')
      ?.eq('team_id', teamId)
      ?.order('due_date', { ascending: true });

    if (error) throw error;

    return data?.map(deliverable => ({
      id: deliverable?.id,
      teamId: deliverable?.team_id,
      deliverableName: deliverable?.deliverable_name,
      dueDate: deliverable?.due_date,
      requiredUploadCount: deliverable?.required_upload_count,
      completedUploadCount: deliverable?.completed_upload_count,
      status: deliverable?.status,
      createdAt: deliverable?.created_at,
      updatedAt: deliverable?.updated_at
    })) || [];
  } catch (error) {
    console.error('Error fetching team deliverables:', error);
    throw new Error(error?.message || 'Failed to fetch deliverables');
  }
};

/**
 * Create a new deliverable for a team
 * @param {Object} deliverableData - Deliverable data
 * @returns {Promise<Object>} Created deliverable
 */
export const createDeliverable = async (deliverableData) => {
  try {
    const { data, error } = await supabase
      ?.from('team_deliverables')
      ?.insert([{
        team_id: deliverableData?.teamId,
        deliverable_name: deliverableData?.deliverableName,
        due_date: deliverableData?.dueDate,
        required_upload_count: deliverableData?.requiredUploadCount || 1
      }])
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      teamId: data?.team_id,
      deliverableName: data?.deliverable_name,
      dueDate: data?.due_date,
      requiredUploadCount: data?.required_upload_count,
      completedUploadCount: data?.completed_upload_count,
      status: data?.status,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  } catch (error) {
    console.error('Error creating deliverable:', error);
    throw new Error(error?.message || 'Failed to create deliverable');
  }
};

/**
 * Update an existing deliverable
 * @param {string} deliverableId - Deliverable UUID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Updated deliverable
 */
export const updateDeliverable = async (deliverableId, updateData) => {
  try {
    const updates = {};
    if (updateData?.deliverableName) updates.deliverable_name = updateData?.deliverableName;
    if (updateData?.dueDate) updates.due_date = updateData?.dueDate;
    if (updateData?.requiredUploadCount !== undefined) updates.required_upload_count = updateData?.requiredUploadCount;

    const { data, error } = await supabase
      ?.from('team_deliverables')
      ?.update(updates)
      ?.eq('id', deliverableId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      teamId: data?.team_id,
      deliverableName: data?.deliverable_name,
      dueDate: data?.due_date,
      requiredUploadCount: data?.required_upload_count,
      completedUploadCount: data?.completed_upload_count,
      status: data?.status,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  } catch (error) {
    console.error('Error updating deliverable:', error);
    throw new Error(error?.message || 'Failed to update deliverable');
  }
};

/**
 * Delete a deliverable
 * @param {string} deliverableId - Deliverable UUID
 * @returns {Promise<boolean>} Success status
 */
export const deleteDeliverable = async (deliverableId) => {
  try {
    const { error } = await supabase
      ?.from('team_deliverables')
      ?.delete()
      ?.eq('id', deliverableId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting deliverable:', error);
    throw new Error(error?.message || 'Failed to delete deliverable');
  }
};

/**
 * Get uploads for a specific deliverable
 * @param {string} deliverableId - Deliverable UUID
 * @returns {Promise<Array>} Array of uploads
 */
export const getDeliverableUploads = async (deliverableId) => {
  try {
    const { data, error } = await supabase
      ?.from('team_deliverable_uploads')
      ?.select(`
        *,
        user_profiles!inner(full_name, email)
      `)
      ?.eq('deliverable_id', deliverableId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(upload => ({
      id: upload?.id,
      deliverableId: upload?.deliverable_id,
      teamId: upload?.team_id,
      uploadedBy: upload?.uploaded_by,
      uploaderName: upload?.user_profiles?.full_name,
      uploaderEmail: upload?.user_profiles?.email,
      fileName: upload?.file_name,
      fileUrl: upload?.file_url,
      fileSize: upload?.file_size,
      fileType: upload?.file_type,
      description: upload?.description,
      createdAt: upload?.created_at,
      updatedAt: upload?.updated_at
    })) || [];
  } catch (error) {
    console.error('Error fetching deliverable uploads:', error);
    throw new Error(error?.message || 'Failed to fetch uploads');
  }
};

/**
 * Get deliverables approaching due date (for notifications)
 * @param {number} hoursThreshold - Hours before due date (default 24)
 * @returns {Promise<Array>} Array of deliverables due soon
 */
export const getDeliverablesDueSoon = async (hoursThreshold = 24) => {
  try {
    const { data, error } = await supabase
      ?.rpc('get_deliverables_due_soon', { hours_threshold: hoursThreshold });

    if (error) throw error;

    return data?.map(item => ({
      deliverableId: item?.deliverable_id,
      teamId: item?.team_id,
      teamName: item?.team_name,
      deliverableName: item?.deliverable_name,
      dueDate: item?.due_date,
      status: item?.status,
      requiredUploadCount: item?.required_upload_count,
      completedUploadCount: item?.completed_upload_count
    })) || [];
  } catch (error) {
    console.error('Error fetching deliverables due soon:', error);
    throw new Error(error?.message || 'Failed to fetch deliverables due soon');
  }
};

export default {
  getTeamDeliverables,
  createDeliverable,
  updateDeliverable,
  deleteDeliverable,
  getDeliverableUploads,
  getDeliverablesDueSoon
};